import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-[#0d0d0d] border border-zinc-700 focus:border-[#E10600] outline-none px-4 py-3 text-white placeholder:text-zinc-600 transition-colors duration-300 text-sm";

export default function TicketsFighter() {
  const [tickets, setTickets] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState("");
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/tickets/mine").then((r) => setTickets(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/tickets", { subject, message });
      toast.success("Ticket raised — the team will reply here");
      setSubject(""); setMessage("");
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const sendReply = async (id) => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${id}/reply`, { message: reply });
      setReply("");
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <section className="border border-zinc-800 bg-[#111111] p-6 sm:p-8" data-testid="tickets-section">
      <p className="font-mono2 text-[10px] tracking-[0.35em] text-zinc-500 uppercase mb-6">Support Tickets</p>

      <form onSubmit={create} className="space-y-4 mb-8" data-testid="ticket-create-form">
        <input className={inputCls} placeholder="Subject — e.g. Question about my license" value={subject} onChange={(e) => setSubject(e.target.value)} required data-testid="ticket-subject-input" />
        <textarea className={`${inputCls} min-h-24`} placeholder="Describe your issue or question..." value={message} onChange={(e) => setMessage(e.target.value)} required data-testid="ticket-message-input" />
        <button type="submit" disabled={busy} data-testid="ticket-create-button" className="font-cond font-semibold tracking-[0.25em] bg-[#E10600] hover:bg-[#C00500] disabled:opacity-50 text-white px-8 py-3 transition-colors duration-300">
          {busy ? "SENDING..." : "RAISE TICKET"}
        </button>
      </form>

      {tickets.length === 0 ? (
        <p className="font-mono2 text-[10px] tracking-[0.3em] text-zinc-600 uppercase" data-testid="tickets-empty">No tickets yet</p>
      ) : (
        <div className="space-y-3" data-testid="tickets-list">
          {tickets.map((t) => (
            <div key={t.id} className="border border-zinc-800" data-testid={`ticket-${t.id}`}>
              <button
                onClick={() => setOpenId(openId === t.id ? null : t.id)}
                className="w-full flex items-center justify-between gap-4 px-4 py-3.5 text-left hover:bg-[#161616] transition-colors"
                data-testid={`ticket-toggle-${t.id}`}
              >
                <span className="font-cond font-semibold text-white uppercase tracking-wide truncate">{t.subject}</span>
                <span className={`font-mono2 text-[9px] tracking-[0.25em] uppercase border px-2.5 py-1 shrink-0 ${t.status === "open" ? "text-yellow-500 border-yellow-800/60" : "text-zinc-500 border-zinc-700"}`} data-testid={`ticket-status-${t.id}`}>
                  {t.status}
                </span>
              </button>
              {openId === t.id && (
                <div className="border-t border-zinc-800 p-4 space-y-3" data-testid={`ticket-thread-${t.id}`}>
                  {t.messages.map((m, i) => (
                    <div key={i} className={`flex ${m.sender === "fighter" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${m.sender === "fighter" ? "bg-[#E10600]/15 border border-[#E10600]/40 text-zinc-100" : "bg-[#1a1a1a] border border-[#D4AF37]/40 text-zinc-200"}`}>
                        <p className="font-mono2 text-[8px] tracking-[0.25em] uppercase mb-1 opacity-60">
                          {m.sender === "fighter" ? "You" : "MPB Team"} · {new Date(m.at).toLocaleString()}
                        </p>
                        {m.text}
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2 pt-2">
                    <input className={inputCls} placeholder="Write a reply..." value={reply} onChange={(e) => setReply(e.target.value)} data-testid={`ticket-reply-input-${t.id}`} />
                    <button onClick={() => sendReply(t.id)} disabled={busy} data-testid={`ticket-reply-button-${t.id}`} className="font-cond font-semibold tracking-[0.2em] bg-[#E10600] hover:bg-[#C00500] disabled:opacity-50 text-white px-6 transition-colors">
                      SEND
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
